/**
 * OrderDetailsModal - Modal de detalles del pedido (versión minimalista)
 *
 * Modal minimalista que muestra información esencial de un pedido específico.
 */
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import {
  faBarcode,
  faCalendar,
  faEnvelope,
  faMapMarkerAlt,
  faPhone,
  faPrint,
  faReceipt,
  faTruck,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef } from 'react';
import { usePrintOrder } from '../../hooks';
import { Order } from '../../models';

interface OrderDetailsModalProps {
  order?: Order;
}

export const OrderDetailsModal = forwardRef<ModalCenterRef, OrderDetailsModalProps>(({ order }, ref) => {
  const { printOrder, isOrderLoading } = usePrintOrder();

  // Función auxiliar para construir URL completa de imagen
  const buildImageUrl = (imageHash: string) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
  };

  // Función para formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Función para formatear precio
  const formatPrice = (price: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(price);
  };

  // Procesar productos del pedido (agrupando por código y sumando cantidades)
  const processOrderProducts = (order: Order) => {
    const productsMap = new Map<string, any>();

    if (!order.lines) return [];

    order.lines.forEach((line: any) => {
      if (line.pack && line.pack.lines) {
        // Si es un pack, descomponer en productos individuales
        const packQuantity = line.qty || 1; // Cantidad de packs

        line.pack.lines.forEach((packLine: any) => {
          if (packLine.product) {
            const code = packLine.product.code;
            // Cantidad total = cantidad del producto en el pack × cantidad de packs
            const totalQuantity = (packLine.qty || 1) * packQuantity;

            if (productsMap.has(code)) {
              // Si ya existe, sumar la cantidad total
              productsMap.get(code).quantity += totalQuantity;
            } else {
              // Si no existe, crear nuevo
              productsMap.set(code, {
                id: packLine.product.id.toString(),
                name: packLine.product.name,
                code: packLine.product.code,
                image: buildImageUrl(packLine.product.image_hash || ''),
                quantity: totalQuantity,
              });
            }
          }
        });
      } else if (line.product) {
        // Si es un producto individual
        const code = line.product.code;
        if (productsMap.has(code)) {
          // Si ya existe, sumar la cantidad
          productsMap.get(code).quantity += line.qty || 1;
        } else {
          // Si no existe, crear nuevo
          productsMap.set(code, {
            id: line.product.id.toString(),
            name: line.product.name,
            code: line.product.code,
            image: buildImageUrl(line.product.image_hash || ''),
            quantity: line.qty || 1,
          });
        }
      }
    });

    // Convertir el Map a array
    return Array.from(productsMap.values());
  };

  if (!order) return null;

  const handlePrint = async () => {
    if (order.id) {
      await printOrder(order.id);
    }
  };

  const products = processOrderProducts(order);

  return (
    <ModalCenter
      ref={ref}
      title={
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faReceipt} className="w-4 h-4" />
          {order.ref}
        </span>
      }
      type="default"
      width="xl"
    >
      <div className="space-y-6">
        {/* Header - Estado y información básica */}
        <div className="flex items-center justify-between pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            {/* Status Badge */}
            <Badge
              variant="outline"
              className={`text-xs border-0 ${
                order.shipment?.processed_at
                  ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
                  : 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300'
              }`}
            >
              <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 mr-1" />
              {order.shipment?.processed_at ? `Preparado ${formatDate(order.shipment.processed_at)}` : 'Por preparar'}
            </Badge>

            {/* Información básica */}
            <span className="text-sm text-muted-foreground">
              {products.length} productos • {formatPrice(order.total || 0, order.currency)}
            </span>
          </div>

          {/* Print Button */}
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={isOrderLoading(order.id)}>
            {isOrderLoading(order.id) ? (
              <div className="w-4 h-4 border border-muted-foreground border-t-transparent rounded-full animate-spin mr-2"></div>
            ) : (
              <FontAwesomeIcon icon={faPrint} className="w-4 h-4 mr-2" />
            )}
            Imprimir
          </Button>
        </div>

        {/* Cliente y Dirección - Dos columnas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Columna Cliente */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">Cliente</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <div className="flex items-center gap-2">
                <span>{order.customer.name || 'Sin nombre'}</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3" />
                  <span>{order.customer.email || 'Sin email'}</span>
                </div>
                {order.customer.phone && (
                  <div className="flex items-center gap-1">
                    <FontAwesomeIcon icon={faPhone} className="w-3 h-3" />
                    <span>{order.customer.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna Dirección */}
          {order.customer.address?.address1 && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="w-3 h-3" />
                Dirección
              </h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>{order.customer.address.address1}</p>
                <p>
                  {order.customer.address.city}, {order.customer.address.province} {order.customer.address.zip}
                </p>
              </div>
            </div>
          )}

          {/* Envío */}
          {order.shipment?.courier && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-1">
                <FontAwesomeIcon icon={faTruck} className="w-3 h-3" />
                Envío
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img
                    src={buildImageUrl(order.shipment.courier.image_hash || '')}
                    alt={order.shipment.courier.name || ''}
                    className="w-6 h-6 object-contain"
                  />
                  <span className="text-sm text-foreground">{order.shipment.courier.name}</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <FontAwesomeIcon icon={faBarcode} className="w-3 h-3" />
                  <span>{order.shipment.tracking_number || 'Sin tracking'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Productos */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Productos ({products.length})</h3>

          <div className="space-y-3 max-h-60 overflow-y-auto">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`flex items-center justify-between py-2 ${
                  index < products.length - 1 ? 'border-b border-border' : ''
                }`}
              >
                {/* Product Info */}
                <div className="flex items-center gap-3 flex-1">
                  <Product
                    id={parseInt(product.id)}
                    name={product.name}
                    src={product.image}
                    code={product.code}
                    showTooltip={true}
                    enableLineClamp={false}
                  />
                </div>

                {/* Quantity */}
                <div className="bg-muted rounded-full px-3 py-1">
                  <span className="text-sm font-medium text-foreground">{product.quantity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer con información adicional */}
        <div className="pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground text-center">
            Creado: {order.order_created_at ? formatDate(order.order_created_at) : 'N/A'}
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

OrderDetailsModal.displayName = 'OrderDetailsModal';
